import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Zap, MessageCircle } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ProductCard from "@/components/catalog/ProductCard";
import HeroDual from "@/components/home/HeroDual";
import BenefitsBanner from "@/components/home/BenefitsBanner";
import HowToBuy from "@/components/home/HowToBuy";
import FeaturedProductsSkeleton from "@/components/home/FeaturedProductsSkeleton";
import SEOHead from "@/components/SEOHead";
import { FloatingWhatsApp } from "@/components/ui/floating-whatsapp";
import { useFeaturedProducts } from "@/hooks/useFeaturedProducts";
import { useCategories } from "@/hooks/useCategories";
import { cn } from "@/lib/utils";

const Home = () => {
  const { data: featuredProducts = [], isLoading: loadingFeatured } = useFeaturedProducts(8);
  const { data: categories = [] } = useCategories();

  // Get top categories for display
  const topCategories = categories.slice(0, 6);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Tekton - Toneladas de Tecnología"
        description="Tu tienda de tecnología en Costa Rica. Cámaras de seguridad, equipos de cómputo, accesorios y más. Asesoría personalizada y envío rápido."
        keywords="tienda tecnología, cámaras seguridad, computadoras, accesorios tecnológicos, Costa Rica, Tekton"
        breadcrumbs={[{ name: "Inicio", url: "/" }]}
      />
      <Header />

      {/* Hero Dual Segments */}
      <HeroDual />

      {/* Benefits Banner */}
      <BenefitsBanner />

      {/* How to Buy Section */}
      <HowToBuy />

      {/* Categories Section */}
      {topCategories.length > 0 && (
        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl md:text-3xl font-bold">
                Categorías Populares
              </h2>
              <Button asChild variant="ghost" className="text-primary">
                <Link to="/catalogo">
                  Ver todas
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
              {topCategories.map((category, index) => (
                <Link
                  key={category.id}
                  to={`/catalogo?category=${category.slug}`}
                  className={cn(
                    "group flex flex-col items-center gap-3 p-4",
                    "bg-card border border-border rounded-lg",
                    "hover:border-primary hover:shadow-card",
                    "transition-all duration-300",
                    "animate-fade-in-scale opacity-0"
                  )}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {category.image_url && (
                    <img
                      src={category.image_url}
                      alt={category.name}
                      loading="lazy"
                      decoding="async"
                      className="w-12 h-12 object-contain group-hover:scale-110 transition-transform"
                    />
                  )}
                  <span className="text-sm font-medium text-center text-foreground group-hover:text-primary transition-colors">
                    {category.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Products */}
      {loadingFeatured ? (
        <FeaturedProductsSkeleton />
      ) : (
        featuredProducts.length > 0 && (
          <section className="py-12 bg-muted">
            <div className="container mx-auto px-4">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg gradient-cta flex items-center justify-center">
                    <Zap className="h-5 w-5 text-white" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold">
                    Productos Destacados
                  </h2>
                </div>
                <Button asChild variant="ghost" className="text-primary hidden sm:flex">
                  <Link to="/catalogo">
                    Ver todo
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {featuredProducts.map((product, index) => (
                  <div
                    key={product.id}
                    className="animate-slide-up opacity-0"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <ProductCard
                      id={product.id}
                      name={product.name}
                      slug={product.slug}
                      price={product.price}
                      salePrice={product.sale_price}
                      image={product.images?.[0] || ""}
                      brand={product.brand?.name}
                      variations={product.variations}
                    />
                  </div>
                ))}
              </div>

              <div className="text-center mt-8 sm:hidden">
                <Button asChild variant="outline">
                  <Link to="/catalogo">
                    Ver todos los productos
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </section>
        )
      )}

      {/* CTA Section */}
      <section className="py-16 bg-primary text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold">
              ¿Necesitás asesoramiento?
            </h2>
            <p className="text-lg text-white/80">
              Nuestro equipo de expertos está listo para ayudarte a elegir los mejores productos tecnológicos para tus necesidades.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" variant="cta">
                <Link to="/contacto">
                  <MessageCircle className="mr-2 h-5 w-5" />
                  Contactanos por WhatsApp
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline-white">
                <Link to="/catalogo">
                  Ver catálogo completo
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
};

export default Home;
